import { useEffect, useRef, useState } from 'react'
import { useFocusTrap } from '../hooks/useFocusTrap'

const GIPHY_KEY = import.meta.env.VITE_GIPHY_KEY || ''

interface GifResult {
  id: string
  title: string
  previewUrl: string
  fullUrl: string
  width: number
  height: number
}

interface GifPickerProps {
  onSelect: (gifUrl: string) => void
  onClose: () => void
}

export default function GifPicker({ onSelect, onClose }: GifPickerProps) {
  const [search, setSearch] = useState('')
  const [gifs, setGifs] = useState<GifResult[]>([])
  const [loading, setLoading] = useState(true)
  const ref = useRef<HTMLDivElement>(null)
  useFocusTrap(ref)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  // Fetch trending on mount
  useEffect(() => {
    fetchGifs('https://api.giphy.com/v1/gifs/trending?api_key=' + GIPHY_KEY + '&limit=20&rating=g')
  }, [])

  // Search with debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!search.trim()) {
      fetchGifs('https://api.giphy.com/v1/gifs/trending?api_key=' + GIPHY_KEY + '&limit=20&rating=g')
      return
    }
    debounceRef.current = setTimeout(() => {
      const q = encodeURIComponent(search.trim())
      fetchGifs(`https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_KEY}&q=${q}&limit=20&rating=g`)
    }, 300)
  }, [search])

  async function fetchGifs(url: string) {
    setLoading(true)
    try {
      const resp = await fetch(url)
      const data = await resp.json()
      setGifs(
        (data.data || []).map((g: Record<string, unknown>) => {
          const images = g.images as Record<string, Record<string, string>>
          return {
            id: g.id as string,
            title: g.title as string,
            previewUrl: images.fixed_width_small?.url || images.fixed_width?.url || '',
            fullUrl: images.original?.url || images.fixed_width?.url || '',
            width: parseInt(images.fixed_width_small?.width || '200'),
            height: parseInt(images.fixed_width_small?.height || '150'),
          }
        })
      )
    } catch {
      setGifs([])
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
      className="absolute bottom-full left-0 mb-2 flex h-[360px] w-[340px] flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-lg"
    >
      {/* Search */}
      <div className="shrink-0 border-b border-zinc-100 px-3 py-2">
        <input
          autoFocus
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search GIFs..."
          className="w-full rounded-lg bg-zinc-50 px-2.5 py-1.5 text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none"
        />
      </div>

      {/* GIF grid */}
      <div className="flex-1 overflow-y-auto p-2">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-xs text-zinc-400">Loading...</div>
        ) : gifs.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-xs text-zinc-400">No GIFs found</div>
        ) : (
          <div className="columns-2 gap-1.5">
            {gifs.map((gif) => (
              <button
                key={gif.id}
                onClick={() => onSelect(gif.fullUrl)}
                className="mb-1.5 block w-full overflow-hidden rounded-lg hover:opacity-80"
              >
                <img
                  src={gif.previewUrl}
                  alt={gif.title}
                  className="w-full rounded-lg"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Attribution */}
      <div className="shrink-0 border-t border-zinc-100 px-3 py-1.5 text-center">
        <span className="text-[9px] text-zinc-400">Powered by GIPHY</span>
      </div>
    </div>
  )
}
